<template>
  <div class="address-editor">
    <!-- No Address State -->
    <div v-if="!currentAddress" class="address-box empty-address">
      <i class="mdi mdi-map-marker-off-outline empty-icon"></i>
      <p class="empty-title">{{ $t("message.order.no_address_yet") }}</p>
      <div class="flex flex-col gap-2 items-center">
        <button @click="newAddress" class="btn-dark-button">
          {{ $t("message.order.new_address") }}
        </button>
        <div v-if="canEdit" class="flex flex-wrap gap-2 mt-2">
          <button
            @click="$emit('select-address')"
            class="selector-button selector-button-address"
          >
            <i class="mdi mdi-map-marker-check"></i>
            {{ $t("message.order.select_address") }}
          </button>
          <button
            v-if="customerInfoautoId"
            @click="$emit('manage-infoauto')"
            class="selector-button selector-button-address"
          >
            <i class="mdi mdi-database-edit"></i>
            {{ $t("message.order.manage_infoauto_addresses_phones") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Address Display/Edit -->
    <div v-else class="address-section">
      <!-- Address Selection Buttons (Always visible when can edit) -->
      <!-- En movil los tres van en una linea como iconos: el texto queda en
           title/aria-label -->
      <div v-if="canEdit && !isEditing" class="selection-buttons mb-3">
        <div class="selection-row flex flex-wrap gap-2">
          <button
            @click="$emit('select-address')"
            class="selector-button selector-button-address"
            :title="$t('message.order.select_address')"
            :aria-label="$t('message.order.select_address')"
          >
            <i class="mdi mdi-map-marker-check"></i>
            <span class="selector-label">
              {{ $t("message.order.select_address") }}
            </span>
          </button>
          <button
            @click="$emit('select-phone')"
            class="selector-button selector-button-phone"
            :title="$t('message.order.select_phone')"
            :aria-label="$t('message.order.select_phone')"
          >
            <i class="mdi mdi-phone-check"></i>
            <span class="selector-label">
              {{ $t("message.order.select_phone") }}
            </span>
          </button>
          <button
            v-if="customerInfoautoId"
            @click="$emit('manage-infoauto')"
            class="text-link text-green-600"
            :title="$t('message.order.manage_infoauto_addresses_phones')"
            :aria-label="$t('message.order.manage_infoauto_addresses_phones')"
          >
            <i class="mdi mdi-database-edit"></i>
            <span class="selector-label">
              {{ $t("message.order.manage_infoauto_addresses_phones") }}
            </span>
          </button>
        </div>
      </div>

      <!-- Display Mode (when not editing) -->
      <div v-if="!isEditing" class="address-display">
        <InfoautoAddressDisplay :address="currentAddress" />

        <!-- En movil el texto largo partia cada boton en dos lineas: ahi se
             muestra la version corta -->
        <div class="address-actions flex flex-row mt-4 gap-2">
          <!-- Edición manual: solo si el cliente no está en Infoauto -->
          <button
            v-if="canEdit && !isInfoautoAddress && !useInfoautoManager"
            @click="startEditing"
            class="btn-dark-button"
          >
            <i class="mdi mdi-pencil"></i>
            {{ $t("message.order.edit_address") }}
          </button>
          <!-- New address: con cliente de Infoauto se crea desde su gestor -->
          <button
            v-if="canEdit && (isInfoautoAddress || useInfoautoManager)"
            @click="newAddress"
            class="btn-dark-button"
            :title="$t('message.order.new_address')"
          >
            <i class="mdi mdi-plus"></i>
            <span class="label-long">
              {{ $t("message.order.new_address") }}
            </span>
            <span class="label-short">
              {{ $t("message.order.new_address_short") }}
            </span>
          </button>
          <!-- Edit in Infoauto button -->
          <button
            v-if="canEdit && isInfoautoAddress"
            @click="$emit('edit-infoauto-address')"
            class="btn-dark-button"
            :title="$t('message.order.edit_infoauto_address')"
          >
            <i class="mdi mdi-pencil"></i>
            <span class="label-long">
              {{ $t("message.order.edit_infoauto_address") }}
            </span>
            <span class="label-short">
              {{ $t("message.order.edit_infoauto_address_short") }}
            </span>
          </button>
        </div>
      </div>

      <!-- Edit Mode (when editing) -->
      <div v-else class="address-edit">
        <!-- Standard Address Form (USA and others) -->
        <StandardAddressForm
          v-if="!isCostaRicaStore"
          :address="editingAddress"
          :can-edit="true"
          :disabled="false"
          :phone-disabled="false"
          :mounted="mounted"
          @update:address="editingAddress = $event"
          @google-address-selected="handleGoogleAddressSelected"
        />

        <!-- Costa Rica Address Form -->
        <CostaRicaAddressForm
          v-else
          :address="editingAddress"
          :can-edit="true"
          :disabled="false"
          :phone-disabled="false"
          @update:address="editingAddress = $event"
        />

        <!-- Save/Cancel Buttons -->
        <div class="flex flex-row mt-4 gap-2">
          <button
            @click="saveChanges"
            class="btn-success-button flex flex-row items-center gap-2"
            :disabled="saving"
          >
            {{ $t("message.order.save_changes") }}
            <BaseMiniSpinner v-if="saving"></BaseMiniSpinner>
          </button>
          <button
            class="btn-dark-button"
            @click="cancelEditing"
            :disabled="saving"
          >
            {{ $t("message.order.cancel") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StandardAddressForm from "@/components/shared/StandardAddressForm.vue";
import CostaRicaAddressForm from "@/components/shared/CostaRicaAddressForm.vue";
import InfoautoAddressDisplay from "@/components/shared/InfoautoAddressDisplay.vue";
import { defaultAreaCode } from "@/constants/infoautoPersona";

export default {
  name: "AddressEditorForm",
  components: {
    StandardAddressForm,
    CostaRicaAddressForm,
    InfoautoAddressDisplay
  },
  props: {
    address: {
      type: Object,
      default: null
    },
    canEdit: {
      type: Boolean,
      default: true
    },
    customerInfoautoId: {
      type: [String, Number],
      default: null
    },
    customerName: {
      type: String,
      default: ""
    },
    saving: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentAddress: null,
      editingAddress: null,
      isEditing: false,
      mounted: false,
      defaultAddress: {
        name: "",
        company: "",
        street1: "",
        street2: "",
        street3: "",
        city: "",
        state: "",
        country: "US",
        phone: "",
        phoneAreaCode: this.storeAreaCode(),
        postalCode: "",
        residential: null,
        addressVerified: null,
        infoautoAddressId: "",
        infoautoPhoneId: "",
        // Costa Rica specific fields
        provincia: "",
        canton: "",
        distrito: "",
        barrio: "",
        codProvincia: "",
        codCanton: "",
        codDistrito: "",
        codBarrio: "",
        tipo3: 0
      }
    };
  },
  computed: {
    isCostaRicaStore() {
      const activeStore = this.$store.getters["general/getActiveStore"];
      return activeStore?.code === "nkr_costa_rica";
    },
    // Con cliente de Infoauto las direcciones se crean/editan/eligen en su
    // gestor; el formulario manual queda solo para clientes sin Infoauto.
    useInfoautoManager() {
      return !!this.customerInfoautoId;
    },
    isInfoautoAddress() {
      return (
        this.currentAddress?.infoautoAddressId !== null &&
        this.currentAddress?.infoautoAddressId !== ""
      );
    },
    isInfoautoPhone() {
      return (
        this.currentAddress?.infoautoPhoneId !== null &&
        this.currentAddress?.infoautoPhoneId !== ""
      );
    },
    hasChanges() {
      // Used by parent to check if there are pending changes
      return this.isEditing;
    }
  },
  watch: {
    address: {
      handler(val) {
        // Only update if not currently editing to avoid overwriting user changes
        if (val && !this.isEditing) {
          this.currentAddress = { ...val };
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.mounted = true;
  },
  methods: {
    startEditing() {
      // Copy current address to editing address with deep copy to avoid reference issues
      this.editingAddress = JSON.parse(JSON.stringify(this.currentAddress));
      // Sin teléfono todavía: arranca con el prefijo del país. Si ya hay
      // número sin código se deja vacío, suele traer el prefijo adentro.
      if (!this.editingAddress.phone && !this.editingAddress.phoneAreaCode) {
        this.editingAddress.phoneAreaCode = this.storeAreaCode();
      }
      this.$nextTick(() => {
        this.isEditing = true;
      });
    },
    cancelEditing() {
      // Discard changes
      this.editingAddress = null;
      this.isEditing = false;
    },
    saveChanges() {
      // Save the edited address
      this.currentAddress = { ...this.editingAddress };
      this.$emit("save", this.currentAddress);
      this.isEditing = false;
      this.editingAddress = null;
    },
    handleGoogleAddressSelected(addressData) {
      // Fill all the address fields from Google data
      if (addressData.street1) {
        this.editingAddress.street1 = addressData.street1;
      }
      if (addressData.city) {
        this.editingAddress.city = addressData.city;
      }
      if (addressData.state) {
        this.editingAddress.state = addressData.state;
      }
      if (addressData.postal_code) {
        this.editingAddress.postalCode = addressData.postal_code;
      }
      if (addressData.country) {
        this.editingAddress.country = addressData.country;
      }
    },
    newAddress() {
      if (this.useInfoautoManager) {
        this.$emit("manage-infoauto");
        return;
      }
      this.createNewAddress();
    },
    createNewAddress() {
      const newAddress = { ...this.defaultAddress };
      if (this.customerName) {
        newAddress.name = this.customerName;
      }
      this.editingAddress = newAddress;
      this.isEditing = true;
    },
    // Public method to be called by parent
    selectAddress(address) {
      this.currentAddress = { ...address };
      this.isEditing = false;
      this.editingAddress = null;

      // Automatically save the selected address
      this.$emit("save", this.currentAddress);
    },
    // Public method to update phone
    updatePhone(phone, phoneId = "", areaCode = "") {
      if (this.currentAddress) {
        this.currentAddress.phone = phone;
        this.currentAddress.phoneAreaCode = areaCode;
        this.currentAddress.infoautoPhoneId = phoneId;
      }
    },
    storeAreaCode() {
      return defaultAreaCode(
        this.$store.getters["general/getActiveStore"]?.code
      );
    }
  }
};
</script>

<style scoped>
.address-editor {
  width: 100%;
}

.empty-address {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 2rem 1rem;
  text-align: center;
}

.empty-icon {
  font-size: 2.25rem;
  color: #6b7280;
}

.empty-title {
  margin: 0 0 0.5rem;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
}

.selection-buttons {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

.address-display {
  padding: 0.75rem 0 0;
}

.address-edit {
  padding: 0.75rem 0 0;
}

.text-link {
  background: none;
  border: none;
  padding: 0 0.5rem;
  min-height: 34px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  border-radius: 8px;
}

.text-link:hover {
  background-color: #f0fdf4;
}

.selector-button {
  min-height: 34px;
  padding: 0 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background-color: white;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s, color 0.15s;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 1rem;
  color: #374151;
}

.selector-button i {
  font-size: 1.125rem;
}

.selector-button-address i {
  color: #2563eb;
}

.selector-button-phone i {
  color: #059669;
}

.selector-button:hover {
  border-color: #9ca3af;
  background-color: #f9fafb;
  color: #111827;
}

.address-actions .btn-dark-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
}

.address-actions .label-short {
  display: none;
}

/* Movil: con texto cada boton ocupaba su propia linea (tres filas antes de
   ver la direccion). Solo iconos, en una linea */
@media (max-width: 767px) {
  .selection-buttons {
    margin-bottom: 0.5rem;
    padding-bottom: 0.5rem;
  }

  .selection-row {
    flex-wrap: nowrap;
  }

  .selection-row .selector-label {
    display: none;
  }

  .selection-row .selector-button,
  .selection-row .text-link {
    justify-content: center;
    width: 44px;
    min-height: 40px;
    padding: 0;
  }

  .selection-row .text-link {
    border: 1px solid #d1d5db;
  }

  .selection-row i {
    font-size: 1.375rem;
  }

  .address-actions {
    margin-top: 0.75rem;
  }

  .address-actions .btn-dark-button {
    flex: 1 1 0;
    min-width: 0;
    min-height: 40px;
    white-space: nowrap;
  }

  .address-actions .label-long {
    display: none;
  }

  .address-actions .label-short {
    display: inline;
  }
}
</style>
